import routes from './routes'

export const HOME_CRUMB = {
  label: 'Inicio',
  path: routes.dashboard,
}

/**
 * Full-trail overrides for routes that should not expose intermediate segments.
 * Example: /pensiones → Inicio > Mis Pensiones
 */
export const BREADCRUMB_OVERRIDES = {
  [routes.pensionCalculations]: [
    { ...HOME_CRUMB },
    { label: 'Mis Pensiones', path: routes.pensionCalculations },
  ],
  [routes.pensionCalculationsEdit]: [
    { ...HOME_CRUMB },
    { label: 'Mis Pensiones', path: routes.pensionCalculations },
    { label: 'Editar', path: routes.pensionCalculationsEdit },
  ],
  [routes.pensionUsers]: [
    { ...HOME_CRUMB },
    { label: 'Usuarios', path: routes.pensionUsers },
  ],
}

/**
 * Human-readable labels for path segments / full paths.
 */
export const BREADCRUMB_LABELS = {
  [routes.dashboard]: 'Inicio',
  [routes.preferences]: 'Preferencias',
  [routes.utm]: 'Unidad Tributaria Mensual (UTM)',
  [routes.utmNew]: 'Nuevo UTM',
  [routes.pensionUsers]: 'Usuarios',
  [routes.pensionCalculations]: 'Mis Pensiones',
  [routes.pensionCalculationsEdit]: 'Editar cálculo',
  [routes.logs]: 'Bitácora',
  [routes.help]: 'Contacto',
  [routes.support]: 'Tickets',
  [routes.onboarding]: 'Bienvenida',
  pension: 'Pensión',
  utm: 'Unidad Tributaria Mensual (UTM)',
  new: 'Nuevo',
  edit: 'Editar',
  calculations: 'Mis Pensiones',
  usuarios: 'Usuarios',
  users: 'Usuarios',
  preferences: 'Preferencias',
  logs: 'Bitácora',
  bitacora: 'Bitácora',
  ayuda: 'Contacto',
  support: 'Tickets',
  soporte: 'Tickets',
  dashboard: 'Inicio',
  inicio: 'Inicio',
  ingresar: 'Ingresar',
  preferencias: 'Preferencias',
  pensiones: 'Mis Pensiones',
  bienvenida: 'Bienvenida',
  nuevo: 'Nuevo',
  editar: 'Editar',
}

const SKIP_SEGMENTS = new Set(['admin'])

const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const titleCase = (value = '') =>
  value
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())

const segmentLabel = (segment, cumulative, menuByPath) => {
  if (UUID_SEGMENT.test(segment)) return 'Detalle'
  return (
    BREADCRUMB_LABELS[cumulative] ||
    menuByPath.get(cumulative) ||
    BREADCRUMB_LABELS[segment] ||
    titleCase(segment)
  )
}

const APP_ROOT_PREFIXES = [
  routes.dashboard,
  routes.preferences,
  routes.utm,
  routes.pensionCalculations,
  routes.pensionUsers,
  '/pension',
  '/usuarios',
  '/users',
  routes.logs,
  routes.help,
  routes.support,
  routes.onboarding,
  '/dashboard',
  '/preferences',
  '/logs',
  '/support',
  '/onboarding',
]

/**
 * Build breadcrumb items from the current pathname.
 * Prefers explicit overrides, then ACL menu names, then known labels.
 */
export const buildBreadcrumbs = (pathname, menus = []) => {
  if (!pathname || pathname === '/') {
    return [{ ...HOME_CRUMB, current: true }]
  }

  const override = BREADCRUMB_OVERRIDES[pathname]
  if (override?.length) {
    return override.map((crumb, index) => ({
      ...crumb,
      current: index === override.length - 1,
    }))
  }

  const menuByPath = new Map(
    menus.filter((menu) => menu?.path).map((menu) => [menu.path, menu.name])
  )

  const segments = pathname.split('/').filter(Boolean)
  const crumbs = []

  const needsHome =
    APP_ROOT_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)) ||
    pathname.startsWith('/admin')

  if (needsHome) {
    crumbs.push({ ...HOME_CRUMB, current: false })
  }

  let cumulative = ''

  segments.forEach((segment) => {
    cumulative += `/${segment}`

    if (SKIP_SEGMENTS.has(segment)) return
    if (cumulative === routes.dashboard) return

    const label = segmentLabel(segment, cumulative, menuByPath)

    crumbs.push({
      label,
      path: cumulative,
      current: false,
    })
  })

  if (!crumbs.length) {
    return [{ ...HOME_CRUMB, current: true }]
  }

  const unique = crumbs.filter(
    (crumb, index, list) => index === 0 || crumb.path !== list[index - 1].path
  )

  return unique.map((crumb, index) => ({
    ...crumb,
    current: index === unique.length - 1,
  }))
}
